import React, { useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { MarketMicrostructureBackdrop } from '../../Access/MarketMicrostructureBackdrop.component';
import { requestMagicLink } from '../../../services/api';
import { useStore } from '../../../store/useStore';
import { trackEvent } from '../../../services/analytics';
import { DataFeed } from '../../DataFeed';
import * as S from './MobileView.style';

interface Props {
  onUpgradeClick: () => void;
}

interface MarketAnalysis {
  summary: string;
  sentiment: string;
  confidence: number;
}

const DRAWER_PEEK = 92;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const formatPrice = (value?: number) =>
  typeof value === 'number'
    ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : 'awaiting';

const formatPct = (value?: number) => (typeof value === 'number' ? `${value.toFixed(1)}%` : 'n/a');

const parseMarketAnalysis = (raw: unknown): MarketAnalysis | null => {
  if (!raw) return null;

  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as MarketAnalysis;
    } catch {
      return null;
    }
  }

  if (typeof raw === 'object') {
    const candidate = raw as Partial<MarketAnalysis>;
    if (candidate.summary) {
      return {
        summary: candidate.summary,
        sentiment: candidate.sentiment || 'neutral',
        confidence: candidate.confidence ?? 0,
      };
    }
  }

  return null;
};

const formatTimeAgo = (timestamp: string) => {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m ago`;
};

const toBlockBar = (probPct: number) => {
  const filled = clamp(Math.round(probPct / 10), 1, 10);
  const empty = 10 - filled;
  return `${'▉'.repeat(filled)}${'░'.repeat(empty)}`;
};

export const MobileView: React.FC<Props> = ({ onUpgradeClick }) => {
  const {
    appState,
    tier,
    blockedReason,
    closeReason,
    expiresAt,
    serverNow,
    previewMode,
    dataFeed,
    aiAnalysis,
    analysisMeta,
  } = useStore();

  const [timeLeft, setTimeLeft] = useState('00:00');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragTranslate, setDragTranslate] = useState<number | null>(null);
  const [showLoginSheet, setShowLoginSheet] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginMessage, setLoginMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const drawerRef = useRef<HTMLElement>(null);
  const whatIsRef = useRef<HTMLElement>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startY: number;
    startTranslate: number;
    maxTranslate: number;
  } | null>(null);

  const marketAnalysis = useMemo(
    () => parseMarketAnalysis(dataFeed.marketAnalysis),
    [dataFeed.marketAnalysis],
  );

  const reachabilityLevels = useMemo(() => {
    const levels = dataFeed.reachability?.levels ?? [];
    return [...levels].sort((a, b) => a.movePct - b.movePct);
  }, [dataFeed.reachability]);

  const showReachability = (tier === 'trial' || tier === 'active') && reachabilityLevels.length > 0;

  const aiBody =
    aiAnalysis?.trim() ||
    marketAnalysis?.summary ||
    'the.ai analysis appears after the first live snapshot reaches your session.';

  const previewSnippet = aiBody.slice(0, 84);
  const timingMeta = analysisMeta?.lastRunAt
    ? `updated ${formatTimeAgo(analysisMeta.lastRunAt)}`
    : 'awaiting analysis';

  useEffect(() => {
    const measure = () => {
      setDrawerHeight(drawerRef.current?.getBoundingClientRect().height ?? 0);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (!expiresAt || !serverNow) {
      setTimeLeft('00:00');
      return;
    }

    const serverTime = new Date(serverNow).getTime();
    const expiresTime = new Date(expiresAt).getTime();
    const offset = Date.now() - serverTime;

    const update = () => {
      const now = Date.now() - offset;
      const remaining = expiresTime - now;
      if (remaining <= 0) {
        setTimeLeft('00:00');
        return;
      }
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
      if (hours > 0) {
        setTimeLeft(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`,
        );
      } else {
        setTimeLeft(
          `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
        );
      }
    };

    update();
    const interval = window.setInterval(update, 1000);
    return () => window.clearInterval(interval);
  }, [expiresAt, serverNow]);

  const maxTranslate = Math.max(0, drawerHeight - DRAWER_PEEK);
  const baseTranslate = drawerOpen ? 0 : maxTranslate;
  const currentTranslate = clamp(
    dragTranslate === null ? baseTranslate : dragTranslate,
    0,
    maxTranslate,
  );

  const startDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (maxTranslate <= 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    dragStateRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startTranslate: currentTranslate,
      maxTranslate,
    };
    setDragTranslate(currentTranslate);
  };

  const moveDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || !dragging || e.pointerId !== dragState.pointerId) return;
    const next = clamp(
      dragState.startTranslate + (e.clientY - dragState.startY),
      0,
      dragState.maxTranslate,
    );
    setDragTranslate(next);
  };

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || e.pointerId !== dragState.pointerId) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const finalTranslate = dragTranslate === null ? dragState.startTranslate : dragTranslate;
    setDrawerOpen(finalTranslate < dragState.maxTranslate * 0.45);
    setDragging(false);
    setDragTranslate(null);
    dragStateRef.current = null;
  };

  const toggleDrawer = () => {
    if (dragging) return;
    setDrawerOpen((prev) => {
      const next = !prev;
      if (next) {
        trackEvent('mobile_ai_drawer_open');
      }
      return next;
    });
  };

  const bootingPreview = appState === 'blocked' && blockedReason === 'new';
  const primaryActionLabel =
    tier === 'trial'
      ? '[ upgrade to pro ]'
      : tier === 'preview'
        ? '[ unlock 24h trial ]'
        : tier === 'active'
          ? '[ pro active ]'
          : bootingPreview
            ? '[ starting preview... ]'
            : '[ start 24h trial ]';

  const conversionCopy =
    tier === 'trial'
      ? 'Trial is active. Convert now to keep reading structure after expiry.'
      : tier === 'active'
        ? 'Mobile gives quick structural context on the move.'
        : tier === 'preview'
          ? `Preview mode running${previewMode ? ` (${previewMode.lag_seconds}s lag).` : '.'} Unlock trial for full-resolution structure.`
          : closeReason === 'trial_expired'
            ? 'Preview ended. Start a new trial or log in to continue.'
            : bootingPreview
              ? 'Preparing your preview environment.'
              : 'Access is paused. Start a 24h trial to read market structure in real time.';

  const productDescriptor =
    'Structural awareness for Bitcoin. Microstructure + regime context + sonic encoding.';

  const tierLabel =
    tier === 'blocked'
      ? closeReason === 'trial_expired'
        ? 'preview ended'
        : bootingPreview
          ? 'starting'
          : 'access required'
      : tier;

  const accessMeta =
    tier === 'preview'
      ? `preview left ${timeLeft}`
      : tier === 'trial'
        ? `trial left ${timeLeft}`
        : tier === 'active'
          ? 'pro access live'
          : closeReason === 'trial_expired'
            ? 'resume access below'
            : 'start with a 24h trial';

  const showRawFeedPanel = tier === 'active' || tier === 'preview' || tier === 'trial';
  const showExplainerPanels = tier !== 'active';

  const nextSteps =
    tier === 'trial'
      ? ['Upgrade to pro to keep full-fidelity access after trial expiry.']
      : [
          'Start a 24h trial to unlock full mobile access.',
          'Already subscribed? Log in with your account email.',
        ];

  const handlePrimaryAction = () => {
    if (tier === 'active' || bootingPreview) return;
    if (tier === 'trial') {
      trackEvent('mobile_upgrade_click_trial');
    } else if (tier === 'preview') {
      trackEvent('mobile_upgrade_click_preview');
    } else if (tier === 'blocked') {
      trackEvent('mobile_upgrade_click_blocked');
    } else {
      trackEvent('mobile_upgrade_click_other');
    }
    onUpgradeClick();
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || loginLoading) return;
    setLoginLoading(true);
    setLoginMessage(null);
    trackEvent('magic_link_request_mobile_attempt');

    try {
      await requestMagicLink(loginEmail);
      trackEvent('magic_link_request_mobile_success');
      setLoginMessage({
        type: 'success',
        text: 'Access link sent. Check your inbox.',
      });
    } catch (error) {
      trackEvent('magic_link_request_mobile_error');
      setLoginMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Unable to send link.',
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleWhatIsClick = () => {
    trackEvent('mobile_what_is_click');
    whatIsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <S.MobileShell>
      <S.MobileBackdrop aria-hidden="true">
        <S.MobileBackdropLayer>
          <MarketMicrostructureBackdrop />
        </S.MobileBackdropLayer>
        <S.MobileBackdropVeil />
      </S.MobileBackdrop>

      <S.MobileContent>
        <S.TopCluster>
          <S.BrandRow>
            <S.BrandChip>
              <S.BrandIcon src="/favicon.svg" alt="semn.ai" />
              <S.BrandText>semn.ai</S.BrandText>
            </S.BrandChip>
          </S.BrandRow>

          <S.ConversionCard>
            <S.ConversionTop>
              <S.ConversionTitle>see and hear Bitcoin</S.ConversionTitle>
              <S.TierPill>{tierLabel}</S.TierPill>
            </S.ConversionTop>
            <S.ConversionTagline>{productDescriptor}</S.ConversionTagline>
            <S.ConversionCopy>{conversionCopy}</S.ConversionCopy>
            <S.ConversionMeta>{accessMeta}</S.ConversionMeta>
            <S.ConversionActions>
              <S.PrimaryAction
                type="button"
                onClick={handlePrimaryAction}
                $fullWidth={tier !== 'blocked' && tier !== 'preview'}
                disabled={tier === 'active' || bootingPreview}
              >
                {primaryActionLabel}
              </S.PrimaryAction>
              {(tier === 'blocked' || tier === 'preview') && (
                <S.SecondaryAction
                  type="button"
                  onClick={() => {
                    trackEvent('mobile_login_sheet_open');
                    setShowLoginSheet(true);
                    setLoginEmail('');
                    setLoginMessage(null);
                    setLoginLoading(false);
                  }}
                >
                  [ log in ]
                </S.SecondaryAction>
              )}
              {tier !== 'active' && (
                <S.TertiaryAction type="button" onClick={handleWhatIsClick}>
                  [ what is semn.ai? ]
                </S.TertiaryAction>
              )}
            </S.ConversionActions>
            <S.DisclaimerLine>No trading signals. Context tool only.</S.DisclaimerLine>
          </S.ConversionCard>

          <S.StatsGrid>
            <S.StatCard>
              <S.StatLabel>price</S.StatLabel>
              <S.StatValue>{formatPrice(dataFeed.lastPrice as number | undefined)}</S.StatValue>
            </S.StatCard>
            <S.StatCard>
              <S.StatLabel>trend</S.StatLabel>
              <S.StatValue>{dataFeed.trendDirection || 'n/a'}</S.StatValue>
            </S.StatCard>
            <S.StatCard>
              <S.StatLabel>time left</S.StatLabel>
              <S.StatValue>{tier === 'active' ? 'pro' : timeLeft}</S.StatValue>
            </S.StatCard>
          </S.StatsGrid>
        </S.TopCluster>

        {showRawFeedPanel && (
          <S.ProRawFeedWrap>
            <DataFeed rawOnly />
          </S.ProRawFeedWrap>
        )}

        {showReachability && (
          <S.ReachabilityCard>
            <S.ReachabilityTitle>
              1H Move Distribution ({dataFeed.reachability?.horizonMinutes || 60}m)
            </S.ReachabilityTitle>
            {reachabilityLevels.map((level) => (
              <S.ReachabilityRow key={`${level.movePct}-${level.probPct}`}>
                <S.ReachabilityMove>{`P(|move| ≥ ${Math.abs(level.movePct)}%)`}</S.ReachabilityMove>
                <S.ReachabilityBlocks>{toBlockBar(level.probPct)}</S.ReachabilityBlocks>
                <S.ReachabilityProb>{level.probPct}%</S.ReachabilityProb>
              </S.ReachabilityRow>
            ))}
          </S.ReachabilityCard>
        )}

        {showExplainerPanels && (
          <>
            <S.ExplainerStack>
              <S.ExplainerCard ref={whatIsRef} id="mobile-what-is">
                <S.ExplainerTitle>what this is</S.ExplainerTitle>
                <S.ExplainerList>
                  <li>Structural awareness: microstructure and regime context, tick by tick.</li>
                  <li>Pressure, imbalance, and transition dynamics, not entries or exits.</li>
                  <li>AI regime synthesis grounded in the same live data you see.</li>
                </S.ExplainerList>
              </S.ExplainerCard>

              <S.ExplainerCard>
                <S.ExplainerTitle>what happens next</S.ExplainerTitle>
                <S.ExplainerSteps>
                  {nextSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </S.ExplainerSteps>
              </S.ExplainerCard>
            </S.ExplainerStack>

            <S.SnapshotCard>
              <S.SnapshotTitle>live terminal preview</S.SnapshotTitle>
              <S.SnapshotGrid>
                <S.SnapshotRow>
                  <S.SnapshotLabel>raw feed</S.SnapshotLabel>
                  <S.SnapshotValue>tick-by-tick stream</S.SnapshotValue>
                </S.SnapshotRow>
                <S.SnapshotRow>
                  <S.SnapshotLabel>pressure</S.SnapshotLabel>
                  <S.SnapshotValue>long/short imbalance</S.SnapshotValue>
                </S.SnapshotRow>
                <S.SnapshotRow>
                  <S.SnapshotLabel>1H move distribution</S.SnapshotLabel>
                  <S.SnapshotValue>probability map</S.SnapshotValue>
                </S.SnapshotRow>
                <S.SnapshotRow>
                  <S.SnapshotLabel>the.ai</S.SnapshotLabel>
                  <S.SnapshotValue>context narrative</S.SnapshotValue>
                </S.SnapshotRow>
              </S.SnapshotGrid>
              <S.SnapshotHint>Desktop unlocks the complete structural workspace.</S.SnapshotHint>
            </S.SnapshotCard>
          </>
        )}

        <S.DesktopFirstNote>
          Mobile is quick context. Desktop is the full structural workspace.
        </S.DesktopFirstNote>

        <S.DrawerFrame ref={drawerRef} $translateY={currentTranslate} $dragging={dragging}>
          <S.DrawerSurface>
            <S.DrawerHandle
              type="button"
              onPointerDown={startDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onClick={toggleDrawer}
            >
              <S.Grabber />
              <S.DrawerPreview>
                <S.DrawerLabel>the.ai</S.DrawerLabel>
                <S.DrawerMeta>{drawerOpen ? timingMeta : previewSnippet}</S.DrawerMeta>
              </S.DrawerPreview>
            </S.DrawerHandle>

            <S.DrawerBody>
              {marketAnalysis && (
                <>
                  <S.DrawerHeadline>
                    {marketAnalysis.sentiment} · conf {marketAnalysis.confidence}/10
                  </S.DrawerHeadline>
                  <S.DrawerSummary>{marketAnalysis.summary}</S.DrawerSummary>
                </>
              )}
              <S.DrawerMarkdown>
                <Markdown>{aiBody}</Markdown>
              </S.DrawerMarkdown>
              <S.DrawerSummary>
                Pressure split: long {formatPct(dataFeed.longPressure as number | undefined)} ·
                short {formatPct(dataFeed.shortPressure as number | undefined)}
              </S.DrawerSummary>
            </S.DrawerBody>
          </S.DrawerSurface>
        </S.DrawerFrame>

        {showLoginSheet && (
          <S.LoginOverlay onClick={() => setShowLoginSheet(false)}>
            <S.LoginSheet onClick={(e) => e.stopPropagation()}>
              <S.LoginTitle>log in</S.LoginTitle>
              <S.LoginCopy>
                Enter your account email. We will send an access link instantly.
              </S.LoginCopy>
              <form onSubmit={handleLoginSubmit}>
                <S.LoginInput
                  type="email"
                  placeholder="you@example.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  autoFocus
                />
                <S.LoginActions>
                  <S.PrimaryAction type="submit" disabled={loginLoading || !loginEmail}>
                    {loginLoading ? '[ sending... ]' : '[ send access link ]'}
                  </S.PrimaryAction>
                  <S.SecondaryAction type="button" onClick={() => setShowLoginSheet(false)}>
                    [ close ]
                  </S.SecondaryAction>
                </S.LoginActions>
              </form>
              {loginMessage && (
                <S.LoginMessage $type={loginMessage.type}>{loginMessage.text}</S.LoginMessage>
              )}
            </S.LoginSheet>
          </S.LoginOverlay>
        )}
      </S.MobileContent>
    </S.MobileShell>
  );
};
